import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { setUserPassword } from '../services/userService';
import type { PasswordResetFormValues } from '../types/user';

const i18nBase = 'resources.user-management';

const PASSWORD_PATTERN =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{12,}$/;

export interface PasswordResetDialogProps {
  email: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const PasswordResetDialog: React.FC<PasswordResetDialogProps> = ({
  email,
  onClose,
  onSuccess,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<PasswordResetFormValues>({
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldFocusError: true,
  });

  useEffect(() => {
    if (email) reset({ password: '', confirmPassword: '' });
  }, [email, reset]);

  const password = watch('password');

  const onSubmit = async (values: PasswordResetFormValues): Promise<void> => {
    if (!email) return;
    setSubmitting(true);
    try {
      await setUserPassword(email, values.password);
      notify(`${i18nBase}.notifications.passwordReset`, { type: 'success' });
      onSuccess();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          `${i18nBase}.notifications.passwordResetFailed`,
          translate
        ),
        { type: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={email !== null}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {translate(`${i18nBase}.dialog.changePassword`)}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Controller
              name="password"
              control={control}
              rules={{
                required: translate(
                  `${i18nBase}.validation.passwordRequired`
                ),
                minLength: {
                  value: 12,
                  message: translate(
                    `${i18nBase}.validation.passwordMinLength`
                  ),
                },
                pattern: {
                  value: PASSWORD_PATTERN,
                  message: translate(
                    `${i18nBase}.validation.passwordPattern`
                  ),
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  label={translate(`${i18nBase}.fields.newPassword`)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    translate(`${i18nBase}.help.passwordPolicy`, {
                      _: "At least 12 characters, with an uppercase, a lowercase, a number and a symbol.",
                    })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: translate(
                  `${i18nBase}.validation.confirmPasswordRequired`
                ),
                validate: (v) =>
                  v === password ||
                  translate(
                    `${i18nBase}.validation.confirmPasswordMismatch`
                  ),
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  type={showConfirmPassword ? 'text' : 'password'}
                  label={translate(`${i18nBase}.fields.confirmPassword`)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowConfirmPassword((v) => !v)
                          }
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: 'none',
                width: { xs: '100%', sm: 140 },
                flex: { xs: '1 1 auto', sm: '0 0 140px' },
                height: 40,
              }}
            >
              {translate('ra.action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || !isValid}
              sx={{
                textTransform: 'none',
                width: { xs: '100%', sm: 140 },
                flex: { xs: '1 1 auto', sm: '0 0 140px' },
                height: 40,
              }}
            >
              {translate('ra.action.save')}
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default PasswordResetDialog;
